import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {
    registerTemplateGraphNode,
    MeshCurve,
    isMaterialLikeNode,
    skipped,
    getTemplateNodeReturns,
    isGroupPart,
    sceneInteractiveMarker,
    EMPTY_LIST,
    TypeDescriptors,
    visitNone,
} from "@cm/template-nodes"
import type {
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    EvaluableTemplateNode,
    BuilderInlet,
    GraphBuilderScope,
    NodeEvaluator,
    INodeSceneInteractionsContext,
    IModifyTemplateGraph,
    TemplateNodePart,
    TemplateNode,
    SceneInteractiveNode,
    MaterialAssignmentConstructorParameters,
    MaterialAssignmentParameters,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {MattressMaterialAssignment} from "#mattress/nodes/mattress-material-assignment"
import {mattressCurveLike} from "#mattress/validation"
import type {MattressCurveLikeNode} from "#mattress/role-markers"
import type {MattressCutData, MattressNodePath} from "#mattress/nodes/component/mattress-component-types"
import {isMattressCurveLikeNode} from "#mattress/role-markers"
import {mattressFabricLayoutToTexUVLayout, getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"
import {findClosestParentComponentGraph} from "#mattress/nodes/mattress"
import {collectMattressNodes} from "#mattress/nodes/component/mattress-component-graph"

import {Matrix4} from "@cm/math"

const TD = TypeDescriptors

const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

export const MattressCutUVChannel = 1

export const mattressFabricLayouts = ["Waterfall", "InvertedWaterfall", "Border", "BorderWithMirror", "AllOverWrap"] as const
export type MattressFabricLayout = (typeof mattressFabricLayouts)[number]
export const mattressFabricLayout: z.ZodMiniType<MattressFabricLayout, MattressFabricLayout> = z.enum(mattressFabricLayouts)

export type MattressCutCondition = {
    curve: MattressCurveLikeNode
    side: "left" | "right"
}

export type MattressCutParameters = {
    name: string
    conditions: MattressCutCondition[]
    materialAssignment: MattressMaterialAssignment | null
    fabricLayout: MattressFabricLayout
}

const definition: TemplateNodeDefinition<MattressCutParameters> = {
    nodeClass: "MattressCut",
    validation: {
        parameters: z.object({
            name: z.string(),
            conditions: z.array(
                z.object({
                    curve: mattressCurveLike,
                    side: z.union([z.literal("left"), z.literal("right")]),
                }),
            ),
            materialAssignment: z.nullable(graphNode(MattressMaterialAssignment)),
            fabricLayout: mattressFabricLayout,
        }),
        returns: getTemplateNodeReturns(),
    },
}

function isValidCutCurve(curve: unknown): curve is MeshCurve {
    if (!(curve instanceof MeshCurve)) return false // TODO
    if (!curve.parameters.closed) return false
    return curve.parameters.controlPoints.length > 2
}

// Class-specific helpers exposed by `MattressCut` for editing the cut's curve conditions.
export interface MattressCutEditingHelpers {
    getValidCurvesForCondition(): MattressCurveLikeNode[]
    addCondition(modifyTemplateGraph: IModifyTemplateGraph): void
    removeCondition(conditionIdx: number, modifyTemplateGraph: IModifyTemplateGraph): void
}

@registerTemplateGraphNode
export class MattressCut
    extends MattressNode<MattressCutParameters>
    implements EvaluableTemplateNode<MattressCutData | null>, MattressCutEditingHelpers, SceneInteractiveNode
{
    get [sceneInteractiveMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressCutParameters> {
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {templateScope} = evaluator
        const {conditions, fabricLayout, materialAssignment} = this.parameters
        const {evaluatedData, nodePath} = mattressContext
        const {cutsDataLists} = evaluatedData

        if (conditions.length === 0) return skipped

        const scope = evaluator.getScope(this)

        const getCutConditionData = (conditionIndex: number) => {
            const condition = conditions[conditionIndex]
            return scope.pureLambda(
                scope.tuple(condition.side, evaluator.evaluate(condition.curve)),
                ([side, curveData]) => {
                    if (!curveData?.localNode || !isValidCutCurve(curveData.localNode)) return null
                    return {
                        side,
                        curveData,
                    }
                },
                `cutconditionData_${conditionIndex}`,
            )
        }
        const cutConditionsData = scope.filterInvalid(scope.list(conditions.map((_, conditionIdx) => getCutConditionData(conditionIdx))))

        const texUVLayout = mattressFabricLayoutToTexUVLayout(fabricLayout)
        const materialAssignmentData = materialAssignment ? scope.phi(evaluator.evaluate(materialAssignment), null) : null

        const newNodePath = scope.pureLambda(scope.tuple(nodePath, this), ([nodePath, instance]) => [...nodePath, instance], "newPath", {
            output: mattressNodePathArrayDesc,
        })
        const cutData = scope.pureLambda(
            scope.tuple(texUVLayout, cutConditionsData, materialAssignmentData, newNodePath),
            ([texUVLayout, cutConditions, materialAssignmentData, newNodePath]) =>
                cutConditions.length
                    ? {
                          texUVLayout,
                          conditions: cutConditions,
                          materialAssignmentData: materialAssignmentData,
                          path: newNodePath,
                      }
                    : null,
            "cutData",
        )

        cutsDataLists.push(
            scope.phi(
                scope.pureLambda(cutData, (cutData) => (cutData ? [cutData] : EMPTY_LIST), "cutsDataList"),
                EMPTY_LIST,
            ),
        )

        const localId = evaluator.getLocalId(this)
        templateScope.alias(cutData, `mattressCutData-${localId}`)

        return visitNone(this.parameters)
    }

    getValidCurvesForCondition(): MattressCurveLikeNode[] {
        const parentGraph = findClosestParentComponentGraph(this)
        const allCurves = Array.from(collectMattressNodes(parentGraph, isMattressCurveLikeNode))
        const usedCurves = this.parameters.conditions.map((condition) => condition.curve)
        return allCurves.filter((curve) => !usedCurves.includes(curve))
    }

    addCondition({modifyTemplateGraph}: IModifyTemplateGraph) {
        const validCurvesForCondition = this.getValidCurvesForCondition()
        if (validCurvesForCondition.length === 0) return

        modifyTemplateGraph(() => {
            this.updateParameters({
                conditions: [...this.parameters.conditions, {side: "left", curve: validCurvesForCondition[0]}],
            })
        })
    }

    removeCondition(conditionIdx: number, {modifyTemplateGraph}: IModifyTemplateGraph) {
        modifyTemplateGraph(() => {
            const newConditions = [...this.parameters.conditions]
            newConditions.splice(conditionIdx, 1)
            this.updateParameters({conditions: newConditions})
        })
    }

    getPartMaterialAssignment(part: TemplateNodePart["part"]): TemplateNode<MaterialAssignmentParameters, MaterialAssignmentConstructorParameters> | undefined {
        if (!isGroupPart(part)) return undefined
        return this.parameters.materialAssignment ?? undefined
    }

    canTransformWith(_nodeParts: TemplateNodePart[]): boolean {
        return false
    }

    canTransformPart(_part: TemplateNodePart["part"], _context: INodeSceneInteractionsContext): boolean {
        return false
    }

    getPartTransform(_part: TemplateNodePart["part"], _context: INodeSceneInteractionsContext): Matrix4 {
        return new Matrix4()
    }

    isDropTargetFor(dropSourceNode: TemplateNode, _dropTargetPart: TemplateNodePart["part"]): boolean {
        return isMaterialLikeNode(dropSourceNode)
    }

    handleDroppedSource(dropSourceNode: TemplateNode, _dropTargetPart: TemplateNodePart["part"], context: INodeSceneInteractionsContext): void {
        if (!isMaterialLikeNode(dropSourceNode)) return // TODO extend to other possible sources

        const materialAssignment = this.parameters.materialAssignment
        if (materialAssignment?.parameters.node === dropSourceNode) return // no change

        context.modifyTemplateGraph(() => {
            this.updateParameters({materialAssignment: new MattressMaterialAssignment({parameters: {node: dropSourceNode, side: "front"}})})
        })
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MattressCutData | null> {
        return scope.unresolvedToNull(evaluator.templateScope.resolve<MattressCutData>(`mattressCutData-${evaluator.getLocalId(this)}`))
    }
}
